import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { CONTACT_TYPE_ID, SUBSCRIPTION_TYPE_ID, denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { translateObjectName } from 'customer-data-objects/record/translateObjectName';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Fragment, useCallback } from 'react';
import { useAssociatedObjectsCardContext } from '../context/AssociatedObjectsCardContext';
import { getAssociationSettingsUrl } from '../helpers/urlHelpers';
import { useValidateCanObjectBeCreated } from '../hooks/useValidateCanObjectBeCreated';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const BuildObjectButton = ({
  associationObjectTypeId,
  associationTypeId,
  associationCategory,
  canEditAssociation,
  canEditAssociationMessageOverride = null,
  createEnabledOverride,
  dataSeleniumTest = 'build-object-button',
  dataTestId = 'build-object-button',
  disabledCreateTabTooltipOverride,
  enableFlexibleAssociations,
  forceSingleSelectAssociation,
  shouldRenderCreateAndAddButton
}) => {
  const {
    objectId,
    objectTypeId,
    toObjectTypeId,
    hasScope
  } = useAssociatedObjectsCardContext();
  const {
    data: validationData,
    loading: validationLoading,
    error: validationError
  } = useValidateCanObjectBeCreated({
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'Associati... Remove this comment to see the full error message
    associationCategory,
    associationTypeId,
    fromObjectId: +objectId,
    fromObjectTypeId: objectTypeId,
    toObjectTypeId
  });
  const isAssociatedRecordsLimitReached = Boolean(!validationLoading && !validationError && validationData && validationData !== 'SUCCESS');
  const isContactToSubscriptionAssociation = objectTypeId === SUBSCRIPTION_TYPE_ID && associationObjectTypeId === CONTACT_TYPE_ID;
  const hasSubscriptionsWriteScope = hasScope('subscriptions-write');
  const hasContactsWriteScope = hasScope('contacts-write');

  /**
   * Contacts and Subscriptions have a special relationship in that changing the contact completely changes where we sent invoices/receipts,
   * and potentially how they are being paid because of payment method on file.
   */
  const isNotContactSubscriptionWriteable = isContactToSubscriptionAssociation && (!hasSubscriptionsWriteScope || !hasContactsWriteScope);
  const sendOpenObjectBuilderMessage = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_OBJECT_BUILDER);
  const handleOpenObjectBuilder = useCallback(() => {
    sendOpenObjectBuilderMessage({
      objectTypeId: associationObjectTypeId,
      association: {
        objectTypeId,
        objectId: +objectId,
        associationTypeId,
        associationCategory
      },
      builderProps: {
        disabledCreateTabTooltipOverride,
        createEnabledOverride,
        enableFlexibleAssociations,
        forceSingleSelectAssociation,
        shouldRenderCreateAndAddButton
      }
    });
  }, [associationCategory, associationObjectTypeId, associationTypeId, objectId, objectTypeId, sendOpenObjectBuilderMessage, disabledCreateTabTooltipOverride, createEnabledOverride, enableFlexibleAssociations, forceSingleSelectAssociation, shouldRenderCreateAndAddButton]);
  const getButtonDisabledMessage = () => {
    if (canEditAssociationMessageOverride !== null && !canEditAssociation) {
      return canEditAssociationMessageOverride;
    } else if (!canEditAssociation || isNotContactSubscriptionWriteable) {
      const subjectObjectName = translateObjectName(denormalizeTypeId(objectTypeId));
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardAssociatedObjects.AssociateObjectButton.tooltip.permissionTooltipText",
        options: {
          subjectObjectName
        }
      });
    }
    if (isAssociatedRecordsLimitReached) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "cardAssociatedObjects.AssociateObjectButton.tooltip.associationLimitReached_jsx",
        elements: {
          Link
        },
        options: {
          href: getAssociationSettingsUrl(objectTypeId, associationObjectTypeId),
          target: '_blank'
        }
      });
    }
    return null;
  };
  const isButtonDisabled = !canEditAssociation || isAssociatedRecordsLimitReached || isNotContactSubscriptionWriteable;
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx(Tooltip, {
      disabled: !isButtonDisabled,
      placement: "left",
      title: getButtonDisabledMessage(),
      children: /*#__PURE__*/_jsx(Button, {
        disabled: isButtonDisabled,
        onClick: handleOpenObjectBuilder,
        use: "link",
        "data-selenium-test": dataSeleniumTest,
        "data-test-id": dataTestId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: isContactToSubscriptionAssociation ? 'cardAssociatedObjects.replaceAssociationButton' : 'cardAssociatedObjects.addAssociationButton'
        })
      })
    })
  });
};
export default BuildObjectButton;